<template>
  <model-edition-form
    ref="form"
    v-model="team"
    :model-name="t('team.title')"
    base-url="/api/teams"
    :model-id="props.modelId"
    :mode="props.mode"
    :with-text="false"
    :includes="['roles']"
    @close="emit('close')"
    @model:updated="emit('model:updated')"
    @model:created="emit('model:created')"
    @model:loaded="orderRoles"
  >
    <v-tabs
      v-model="tab"
      class="ms-4 mt-2"
    >
      <smpw-tab
        value="info"
        :active="tab === 'info'"
        :text="t('team.title')"
      />
      <smpw-tab
        value="members"
        :active="tab === 'members'"
        :text="t('role.member', 2)"
      />
    </v-tabs>

    <v-window
      v-model="tab"
      :touch="false"
    >
      <v-window-item value="info">
        <v-container>
          <v-text-field
            v-model="team.name"
            :readonly="readOnly"
            :disabled="!can('manage_teams')"
            :label="t('global.name')"
            :error="!!validationErrors.name"
            :error-messages="validationErrors.name"
          />
          <user-select
            v-if="can('manage_teams')"
            v-model="team.overseer_user_id"
            :disabled="readOnly"
            :label="t('team.overseer')"
            :error="!!validationErrors.overseer_user_id"
            :error-messages="validationErrors.overseer_user_id"
            active-only
          />

          <v-text-field
            v-if="can('manage_teams')"
            v-model="team.overseer_title"
            :readonly="readOnly"
            :label="t('team.overseer_title')"
            :error="!!validationErrors.overseer_title"
            :error-messages="validationErrors.overseer_title"
          />

          <rich-text-editor

            v-model="team.description"
            :label="t('global.description')"
            :error="!!validationErrors.description"
            :error-messages="validationErrors.description"
          />

          <v-text-field
            v-if="can('manage_teams')"
            v-model="team.order"
            type="number"
            :label="t('location.order')"
            :hint="t('team.order_hint')"
            :error="!!validationErrors.order"
            :error-messages="validationErrors.order"
            hide-details
            class="mb-3"
          />

          <v-checkbox
            v-model="team.description_visible"
            :label="t('team.allow_all_members_hint')"
            :readonly="readOnly"
            class="text-font"
            :error="!!validationErrors.description_visible"
            :error-messages="validationErrors.description_visible"
          />

          <model-check-box
            v-if="can('manage_teams')"
            v-model="team.selected_roles"
            index-route="/api/roles"
            :label="t('team.role_label_category')"
            :text-key="[
              ['caption', ' - '],
              ['description', ''],
            ]"
          />
        </v-container>
      </v-window-item>
      <v-window-item value="members">
        <team-member-selector v-model="team.members" />
      </v-window-item>
    </v-window>
  </model-edition-form>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import RichTextEditor from '@/components/rich-text/RichTextEditor.vue';
import { usePermissions } from '@/composables';
import { useAdminStore } from '@/stores/admin';
import { useErrorStore } from '@/stores/errors';
import ModelCheckBox from '../crud/ModelCheckBox.vue';
import ModelEditionForm from '../ModelEditionForm.vue';
import UserSelect from '../users/UserSelect.vue';
import TeamMemberSelector from './TeamMemberSelector.vue';

const props = defineProps<{
  modelId?: number;
  mode: 'edit' | 'create' | 'show';
}>();
const emit = defineEmits(['model:updated', 'model:created', 'model:loaded', 'error:handled', 'close']);
const { t } = useI18n();
const tab = ref('info');
const { can } = usePermissions();
const readOnly = computed(() => props.mode === 'show');
const defaultItem = {
  id: null,
  name: '',
  description: '',
  overseer_title: '',
  overseer_user_id: null,
  members: [],
  roles: [],
  selected_roles: [],
  order: 0,
  description_visible: false,
  created_at: null,
  created_by: null,
  updated_at: null,
  updated_by: null,
};

const team = ref<App.Models.Team>(defaultItem);
function orderRoles() {
  team.value.selected_roles = team.value.roles.reduce(
    (previous, current) => {
      previous.push(current.id);
      return previous;
    },
    [],
  );
}
const form = ref();
const errorStore = useErrorStore();
const { loadAllUsers } = useAdminStore();

const { validationErrors } = storeToRefs(errorStore);
watch(team, () => {
  if (form.value) {
    form.value.validate();
  }
}, { deep: true });
loadAllUsers([{
  column: 'active',
  operator: '=',
  value: 1,
}]);
</script>
